import { create } from 'zustand';
import type { OutboxObservationItem } from '../api/outbox/types';
import * as syncEngine from '../api/outbox/syncEngine';
import { useAuthStore } from './authStore';
import { usePostsStore } from './postsStore';

interface OutboxState {
  // Only the current user's items — the engine's queue is per-device and
  // may hold another (or no) signed-in user's items; see rawItems below.
  items: OutboxObservationItem[];
  pendingCount: number;
  hydrated: boolean;

  hydrate: () => Promise<void>;
  retry: (id: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /**
   * Passthrough to the sync engine's enqueueObservation, kept on the store
   * so screens reach it via component -> store -> api layering instead of
   * importing src/api/outbox directly. See app/post/create.tsx.
   */
  enqueue: (
    args: Parameters<typeof syncEngine.enqueueObservation>[0]
  ) => Promise<OutboxObservationItem>;
  /**
   * Passthrough to the sync engine's requestDrain, kept on the store for the
   * same reason as enqueue — app/_layout.tsx's lifecycle triggers stay
   * store-only.
   */
  drain: () => void;
  reset: () => void;
}

// The engine's full (unfiltered) queue, cached so auth changes can re-filter
// without waiting for the engine to emit a new snapshot. Held outside
// zustand state for the same reason as postsStore's mapPostsUnsub — these
// aren't values a component should ever render from directly.
let rawItems: OutboxObservationItem[] = [];
// Idempotency guard for hydrate() below. The subscriptions it sets up are
// never torn down — unlike postsStore's mapPostsUnsub/feedUnsub (which
// follow screen mount/unmount), the outbox and auth wiring are meant to
// live for the whole app session, so there's no unsubscribe to hold onto.
let hydrateStarted = false;

const filterForCurrentUser = (uid: string | undefined): OutboxObservationItem[] =>
  uid ? rawItems.filter((it) => it.authorId === uid) : [];

export const useOutboxStore = create<OutboxState>((set) => ({
  items: [],
  pendingCount: 0,
  hydrated: false,

  hydrate: async () => {
    if (hydrateStarted) return;
    hydrateStarted = true;

    syncEngine.subscribeItems((items) => {
      rawItems = items;
      const uid = useAuthStore.getState().user?.id;
      const filtered = filterForCurrentUser(uid);
      set({ items: filtered, pendingCount: filtered.length });
    });
    syncEngine.subscribePostSynced((post) => {
      usePostsStore.getState().ingestSyncedPost(post);
    });
    // Re-filter on sign-in/out without waiting for the engine to notify —
    // items are "keep but hide": still on disk under the other uid, just
    // not shown until that user is signed in again.
    useAuthStore.subscribe((state) => {
      const filtered = filterForCurrentUser(state.user?.id);
      set({ items: filtered, pendingCount: filtered.length });
    });

    await syncEngine.hydrate();
    set({ hydrated: true });
  },

  retry: async (id: string) => {
    await syncEngine.retryItem(id);
  },

  remove: async (id: string) => {
    await syncEngine.deleteItem(id);
  },

  enqueue: (args) => syncEngine.enqueueObservation(args),

  drain: () => syncEngine.requestDrain(),

  reset: () => {
    // Deliberately does NOT unsubscribe from the engine or delete persisted
    // items — the outbox is durable, per-uid storage that survives
    // sign-out (see docs/plans — Durable Offline Outbox). This only clears
    // in-memory UI state; the still-active engine subscription (or the next
    // hydrate()) repopulates it correctly filtered for whoever signs in
    // next.
    set({ items: [], pendingCount: 0, hydrated: false });
  },
}));
